/**
 * The twin a dive opens and the geometry that ties it to the globe view.
 *
 * Asset. An antenna record with a twin mapping (`antenna.twin.assetId`, the
 * PLAN 5.4 cross-link) opens that asset. The loader data does not carry the
 * field yet, so today every dive builds the parametric spec of the antenna
 * (assetSpecForAntenna) and registers it as `antenna_<antennaId>` with the
 * site, a heading equal to the dish's pointing azimuth at the start of the
 * dive (the dish rests facing +Z, so the model opens already facing its
 * satellite) and a main pose that looks at the site from the globe camera's
 * side.
 *
 * Bearing. GlobeEngine.flyTo ends with Cesium's default flight orientation:
 * heading 0 (north up) looking straight down. The twin camera keeps that
 * bearing: it stands on the far side of the dish from the way the globe camera
 * faced (south of the dish for heading 0) and looks along the same heading.
 */
import type { Antenna } from '@/features/loader/types';
import { assetSpecForAntenna, proceduralSpecForAntenna } from '@/features/twin/data/antennaSpec';
import { elevationAxisHeight, estimateAntennaBounds } from '@/features/twin/procedural/dimensions';
import { DEFAULT_FOV, fitDistance, orbitPose, type Vec3 } from '@/features/twin/scene/fit';
import { compassToAssetAzimuth, normaliseDeg } from '@/features/twin/scene/pointing';
import type { AssetSpec, CameraPose } from '@/features/twin/types';

export const DIVE_ASSET_PREFIX = 'antenna_';
/** Heading of the globe camera when the settle flight ends (Cesium's default flight orientation). */
export const GLOBE_SETTLE_HEADING_DEG = 0;
/** Twin camera elevation for the frame that replaces the top-down globe view. */
export const TWIN_ARRIVAL_ELEVATION_DEG = 72;
/** Twin camera elevation the arrival descends to (and the registered main pose). */
export const TWIN_HERO_ELEVATION_DEG = 24;

export function diveAssetId(antennaId: string): string {
  return `${DIVE_ASSET_PREFIX}${antennaId}`;
}

/** The antenna's twin asset id from `antenna.twin.assetId`, or null when the record has no mapping. */
export function twinAssetIdOf(antenna: Antenna): string | null {
  const twin = (antenna as Antenna & { twin?: { assetId?: unknown } | null }).twin;
  const id = twin?.assetId;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/** Height of the dish's top above the ground (elevation axis plus a radius), metres. */
export function dishTopHeightM(antenna: Antenna): number {
  const spec = proceduralSpecForAntenna(antenna);
  return elevationAxisHeight(spec.diameterM, spec.pedestal) + spec.diameterM / 2;
}

/** Compass bearing from the dish to the twin camera for a globe camera heading. */
export function twinCameraBearingDeg(globeHeadingDeg: number = GLOBE_SETTLE_HEADING_DEG): number {
  return normaliseDeg(globeHeadingDeg + 180);
}

/**
 * Camera pose looking at `target` along the globe camera's heading, at the
 * given elevation and distance, in the frame of an asset whose +Z axis points
 * at `assetHeadingDeg`.
 */
export function diveCameraPose(
  target: Vec3,
  distance: number,
  elevationDeg: number,
  assetHeadingDeg: number,
  globeHeadingDeg: number = GLOBE_SETTLE_HEADING_DEG,
  fov: number = DEFAULT_FOV,
): CameraPose {
  const azimuth = compassToAssetAzimuth(twinCameraBearingDeg(globeHeadingDeg), assetHeadingDeg);
  return orbitPose(target, azimuth, elevationDeg, distance, fov);
}

/** Parametric spec for a dive into an antenna without a twin mapping. */
export function buildDiveAssetSpec(antenna: Antenna, headingDeg: number): AssetSpec {
  if (!Number.isFinite(headingDeg)) throw new Error(`buildDiveAssetSpec: heading ${headingDeg} is not finite`);
  const base = assetSpecForAntenna(antenna);
  const { centre, radius } = estimateAntennaBounds(base.procedural);
  const heading = normaliseDeg(headingDeg);
  const main = diveCameraPose(centre, fitDistance(radius, DEFAULT_FOV), TWIN_HERO_ELEVATION_DEG, heading);
  return {
    ...base,
    id: diveAssetId(antenna.id),
    poses: { ...base.poses, main },
    site: { antennaId: antenna.id, lat: antenna.location.lat, lon: antenna.location.lon, headingDeg: heading },
  };
}
